import "./myAssetsTable.scss";

import { Col, Flex, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { SortDirection, Table, TableSpacing, useSort } from "@amzn/stencil-react-components/table";
import { buildColumnDefinitions, buildContent } from "common/helpers/table-utils";
import { ColumnSetting } from "common/types/config-model";
import React, { FC } from "react";

import mySoftwareAssetsTableConfig from "./config/my-software-assets.table-config.json";
import { DUMMY_ASSET_DATA } from "./data/dummy-data";
import { AssetData, ExpandableContentData, ExpandableContentProps } from "./types/asset-types.types";

/**
 * Renders a table with my software assets.
 * @returns A table with my software assets
 */
export const MySoftwareAssetsTable: FC = () => {
  const columns: ColumnSetting[] = mySoftwareAssetsTableConfig.columns;

  const { activeSortId, sortDirection, onSort } = useSort({
    defaultSortDirection: SortDirection.None,
    defaultActiveSortId: "software",
    onSortChange: (_sortId, _sortDir) => {
      /**
       * Notes: the current implementation does not handle sorting on the server.
       * This is a temporary solution to demonstrate sorting on the client.
       * you would need to:
       * 1. Implement sorting on the server.
       * 2. Pass the sort parameters to the API call.
       * 3. Update the table data with the sorted data returned from the API.
       *
       * **remove "_" before "_sortId" and "_sortDir" **
       *
       * Story: https://sim.amazon.com/issues/Manatee-1126
       *
       *
       */
      /* no-op */
    },
  });

  // Use dummy data from the API (here, locally sorted for demonstration).
  const data = DUMMY_ASSET_DATA;

  // Temporary compare function to simulate local sorting.
  // Maps the activeSortId to a property on the asset data.

  // Disabling istanbul since this a temporary function

  /**
   * Notes: the current implementation has no sorting on the server.
   * This is a temporary solution to demonstrate sorting on the client.
   * you would need to:
   * 1. Implement sorting on the server
   * 2. Pass the sort parameters to the API call.
   * 3. Update the table data with the sorted data returned from the API.
   * 4. Remove the istanbul ignore next (or the entire function)
   *
   * Story: https://sim.amazon.com/issues/Manatee-1126
   *
   */
  /* istanbul ignore next */
  const compare = (a: { data: AssetData }, b: { data: AssetData }) => {
    if (sortDirection === SortDirection.None) return 0;

    let aValue: string | number, bValue: string | number;

    switch (activeSortId) {
      case "software":
        aValue = a.data.assetName as string;
        bValue = b.data.assetName as string;
        break;
      case "activation_date":
        // Convert date strings to timestamps for comparison.
        aValue = new Date(a.data.activationDate).getTime();
        bValue = new Date(b.data.activationDate).getTime();
        break;
      default:
        return 0;
    }
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  };

  /**
   * Note: using istanbul ignore next since this is a temporary function
   */
  /* istanbul ignore next */
  const sortedData =
    sortDirection === SortDirection.Descending ? [...data].sort(compare).reverse() : [...data].sort(compare);

  // Build column definitions with actions column for assets table
  const currentColumnDefinitions = buildColumnDefinitions(columns, { includeActionsColumn: true });

  const expandableContent = ({ data, index }: ExpandableContentProps) => {
    const asset: ExpandableContentData = data;
    return buildContent(asset, index);
  };

  const getRowAttributes = () => ({
    expandableContent,
    disabled: false,
  });

  /**
   * Note: the current implementation does not handle loading state.
   * This is a temporary solution to demonstrate loading state.
   * you would need to:
   * 1. Implement loading state.
   * 2. Remove istanbul ignore next
   * 3. Create/update tests
   *
   * Story: https://sim.amazon.com/issues/Manatee-1126
   */
  const isLoading = false;
  /* istanbul ignore next */
  if (isLoading) {
    return (
      <Flex flexDirection="column">
        <View alignSelf="center">
          <Spacer height={32} width={32} />
          <Spinner size={SpinnerSize.Medium} showText loadingText="Loading your assets" />
        </View>
      </Flex>
    );
  }

  return (
    <Col gridGap={32}>
      <Table
        actionHeader="Detail"
        columns={currentColumnDefinitions}
        data={sortedData}
        spacing={TableSpacing.Default}
        getRowAttributes={getRowAttributes}
        dataTestId="my-software-assets-table"
        activeSortId={activeSortId}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    </Col>
  );
};
